import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Modal } from '@/components/Modal';
import { ProductDetail } from '@/components/ProductDetail';
import type {
  ProductMediaFragment,
  ProductVariantWatchBasicDetailsFragment,
} from '@/graphql/saleor/api';
import { getDropdownValue } from '@/lib/attributes';
import { captureError } from '@/lib/errors';
import { getProductType } from '@/lib/product';
import { fetchProductDetail } from '@/lib/product/api';
import type { Maybe } from '@/lib/types';
import { splitStringOfWords } from '@/lib/util';
import { ContactUsNotice } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';
import { messages } from '@/views/ContactUsView/messages';

import { Loader } from '../Loader';
import { ProductCard } from '../ProductCard';

import LogoSvg from '@/assets/breitling-simple.svg';

import { filtersModalMessages } from '../ListingFilters/components/Filters/messages';
import * as S from './ContactUsModal.styles';

export interface ContactUsModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  sku?: Maybe<string>;
}

type ContactUsModalPropsSkuAndOnclose = Pick<
  ContactUsModalProps,
  'onClose' | 'sku'
>;
type DefaultContactUsModalChildrenAndOnclose = Pick<
  ContactUsModalProps,
  'children' | 'onClose'
>;
const ContactUsWithSkuModal = ({
  onClose,
  sku = '',
}: ContactUsModalPropsSkuAndOnclose) => {
  const { formatMessage } = useIntl();
  const {
    formatPrice,
    query: { channel, languageCode },
  } = useRegionProvider();
  const [productVariant, setProductVariant] =
    useState<ProductVariantWatchBasicDetailsFragment>();
  const [productLoading, setProductLoading] = useState(false);
  useEffect(() => {
    initWatchDetails();
  }, []);
  const initWatchDetails = async () => {
    setProductLoading(true);
    try {
      const data = await fetchProductDetail({
        sku: sku?.toUpperCase() ?? '',
        channel,
        languageCode,
      });
      if (!data?.productVariant) return;
      setProductVariant(data?.productVariant);
    } catch (error) {
      captureError(error as Error, {
        extra: {
          sku,
          channel,
          languageCode,
        },
      });
    } finally {
      setProductLoading(false);
    }
  };
  const productType = getProductType(productVariant?.product?.productType);
  const media: ProductMediaFragment | undefined = productVariant?.product?.media
    ? productVariant?.product?.media[0]
    : (productVariant?.product?.thumbnail as ProductMediaFragment);
  const caseMaterial =
    productVariant?.product?.caseMaterial?.values[0]?.translation?.plainText;
  const collectionName = productVariant?.product?.collections?.[0]?.name;
  const strapMaterial =
    productVariant?.product?.strapMaterial?.values[0]?.translation?.plainText;
  const name = productVariant?.product?.name;
  const materialName = productType === 'watch' ? caseMaterial : strapMaterial;
  const getTranslatedName = () => {
    if (productType === 'watch') {
      if (!name || !collectionName) return;
      const normalizedProductNameWithCollection = collectionName
        .replace(/\s+/g, '')
        .toLowerCase();
      const normalizedProductNameWithoutCollection = name
        .replace(/\s+/g, '')
        .toLowerCase();

      const index = normalizedProductNameWithoutCollection.indexOf(
        normalizedProductNameWithCollection,
      );

      if (index !== -1) {
        const beforeMatch = collectionName.slice(0, index);
        const match = name.slice(index, index + collectionName.length + 1);
        const afterMatch = name.slice(index + collectionName.length + 1);

        return (
          <>
            {beforeMatch}
            <ProductDetail.PromotedName data-testid='ProductDetailPromotedName'>
              {match}
            </ProductDetail.PromotedName>
            {afterMatch}
          </>
        );
      } else {
        return <>{collectionName}</>;
      }
    } else if (productType === 'strap') {
      const [coloredWords, restWords] = splitStringOfWords(name as string, 1);
      const lugSize = getDropdownValue(productVariant?.product.lug);

      return (
        <>
          <ProductDetail.PromotedName data-testid='ProductDetailPromotedName'>
            {coloredWords}
          </ProductDetail.PromotedName>{' '}
          {restWords} {lugSize && `- ${lugSize}`}
        </>
      );
    } else if (productType === 'eyewear') {
      return (
        productVariant?.product?.eyewearModel?.values?.[0]?.translation
          ?.plainText ?? name
      );
    } else {
      return name;
    }
  };

  if (productLoading || !productVariant)
    return <Loader data-testid='ProductQueryLoading' />;
  /**
   * Reverses the format of a price and adjusts its elements order based on the format.
   * For example, converts `['USD', '', '4', ',', '900']` to `['4', ',', '900', '', 'USD']`.
   */
  const reverseFormatPrice = () => {
    // Extract child elements from the formatted price using formatPrice function
    const formattedPriceElements = formatPrice(
      productVariant?.pricing?.price?.gross,
    ).props.children;
    // Example output: ['USD', '', '4', ',', '900']
    // Destructure formatted price elements into specific components
    const [currencyElement, spaceCharacter, ...remainingElements] =
      formattedPriceElements;

    // Otherwise, return the elements in their original order
    return [currencyElement, spaceCharacter, ...remainingElements];
  };
  return (
    <>
      <Modal.Header data-testid='ModalContactUsHeader' onClose={onClose}>
        <S.ContactUsTitle>{formatMessage(messages.contactUs)}</S.ContactUsTitle>
      </Modal.Header>
      <S.ContactUsModalBody data-testid='ModalContactUsWithSkuBody'>
        <S.ProductCardBox>
          <S.ProductImageBox>
            <ProductCard.AlternateImg
              alt={`${productType} ${sku}`}
              className={`${productType}-card`}
              data-testid='ProductImageAlternateImg'
              loading='lazy'
              media={media}
              productType={productType}
              sizes='(max-width: 600px) 25vw, 150vw'
              withBackground={false}
            />
          </S.ProductImageBox>

          <S.ProductInfoBox>
            <S.ProductName>{getTranslatedName()}</S.ProductName>
            <S.ProductParametersBox data-testid='ProductInfoBox'>
              <S.ProductParametersItem>{materialName}</S.ProductParametersItem>
              <S.ProductParametersItem>
                {`${formatMessage(
                  filtersModalMessages.reference,
                )}: ${sku?.toLocaleUpperCase()}`}
              </S.ProductParametersItem>
              {productVariant?.pricing && (
                <S.ProductParametersItem>
                  {`${formatMessage(filtersModalMessages.price)}: `}
                  {reverseFormatPrice()}
                </S.ProductParametersItem>
              )}
            </S.ProductParametersBox>
            <S.ContactUsChildenBox>
              <ContactUsNotice></ContactUsNotice>
            </S.ContactUsChildenBox>
          </S.ProductInfoBox>
        </S.ProductCardBox>
      </S.ContactUsModalBody>
    </>
  );
};

const DefaultContactUsModal = ({
  children,
  onClose,
}: DefaultContactUsModalChildrenAndOnclose) => {
  return (
    <>
      <Modal.Header data-testid='ModalContactUsHeader' onClose={onClose}>
        <S.Logo
          alt='Breitling logo'
          data-testid='ModalContactUsLogo'
          height={50}
          src={LogoSvg}
          width={50}
        />
      </Modal.Header>
      <Modal.Body data-testid='ModalContactUsBody'>
        <S.Body data-testid='ModalContactUsBodyContent'>{children}</S.Body>
      </Modal.Body>
    </>
  );
};
export const ContactUsModal = ({
  children,
  isOpen = false,
  onClose,
  sku,
}: ContactUsModalProps) => {
  return (
    <Modal
      data-testid='ModalContactUs'
      isOpen={isOpen}
      maxWidth='xs'
      onClose={onClose}
      width='100%'
    >
      {sku ? (
        <ContactUsWithSkuModal
          onClose={onClose}
          sku={sku}
        ></ContactUsWithSkuModal>
      ) : (
        <DefaultContactUsModal onClose={onClose}>
          {children}
        </DefaultContactUsModal>
      )}
    </Modal>
  );
};

ContactUsModal.displayName = 'ContactUsModal';
